<script>
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
import MoneyServiceDefault, { DOUBLE_ZERO } from "@/services/_bit/MoneyService";

const moneyService = new MoneyServiceDefault();

const roundTo = (value, decimalPlaces) => {
  const factor = 10 ** decimalPlaces;

  return Math.round(value * factor) / factor;
};

const formatDisplayValue = (value, decimalPlaces) => {
  const { integer, penny, delimiter } = moneyService.separatedMoney(value, decimalPlaces);

  if (decimalPlaces === 0) {
    return integer;
  }

  return penny === DOUBLE_ZERO ? integer : `${integer}${delimiter}${penny}`;
};

export default {
  name: "AnimatedNumber",

  props: {
    value: {
      type: [String, Number],
      default: 0,
    },

    duration: {
      type: Number,
      default: 700,
    },

    decimalPlaces: {
      type: Number,
      default: null,
    },
  },

  setup(props) {
    const number = ref(0);
    const activeDecimalPlaces = ref(0);
    let rafId;

    const getTarget = (value) => Number(String(value).replace(/[^\d.-]/g, "")) || 0;
    const getPrefix = (value) => (String(value).startsWith("$") ? "$" : "");

    const resolveDecimalPlaces = (target) => {
      if (props.decimalPlaces !== null && props.decimalPlaces !== undefined) {
        return props.decimalPlaces;
      }

      return Math.abs(target % 1) > 1e-9 ? 2 : 0;
    };

    const prefix = ref(getPrefix(props.value));

    const formattedNumber = computed(() =>
      formatDisplayValue(number.value, activeDecimalPlaces.value)
    );

    function animate(from, to) {
      cancelAnimationFrame(rafId);

      const start = performance.now();
      const decimalPlaces = resolveDecimalPlaces(to);

      activeDecimalPlaces.value = decimalPlaces;

      function update(now) {
        const progress = Math.min((now - start) / props.duration, 1);
        const raw = from + (to - from) * progress;

        number.value =
          decimalPlaces > 0 ? roundTo(raw, decimalPlaces) : Math.floor(raw);

        if (progress < 1) {
          rafId = requestAnimationFrame(update);
        } else {
          number.value = to;
        }
      }

      rafId = requestAnimationFrame(update);
    }

    watch(
      () => props.value,
      (newValue, oldValue) => {
        prefix.value = getPrefix(newValue);
        animate(getTarget(oldValue), getTarget(newValue));
      }
    );

    onMounted(() => {
      prefix.value = getPrefix(props.value);
      animate(0, getTarget(props.value));
    });

    onBeforeUnmount(() => {
      cancelAnimationFrame(rafId);
    });

    return {
      formattedNumber,
      prefix,
    };
  },
};
</script>

<template>
  <span>{{ prefix }}{{ formattedNumber }}</span>
</template>
